/**
 * PAY-4392: which web release is this tab running?
 *
 * `NEXT_PUBLIC_RELEASE_ID` / `NEXT_PUBLIC_RELEASE_BUILT_AT` are baked in by
 * `next.config.mjs` (the Vercel git SHA and the build time). Local dev has no
 * SHA and reports no release, so it never claims.
 *
 * DEV-ONLY OVERRIDE. When `nodeEnv` is `development`, the query string may
 * stand in for a new build: `?__release=<id>&__built=<iso>` sets the release,
 * `__seed=<n>` pins the animation's random seed, `__style=<name>` pins one
 * style, and `__cycle=1` plays the next style when one
 * finishes (never the same twice in a row). This exists so the feature can
 * be exercised (and recorded) without rebuilding for every release. In any
 * other environment the query string is ignored entirely — a production URL
 * cannot fake a release or steer the animation.
 */

import {
  type CelebrationStyle,
  isCelebrationStyle,
} from './celebration-variety';

export const WEB_RELEASE_TRACK = 'web';

export interface RunningRelease {
  releaseId: string;
  builtAt: string;
  /** Dev-only: a fixed animation seed. Always undefined outside development. */
  seed?: number;
  /** Dev-only: play this style. Always undefined outside development. */
  style?: CelebrationStyle;
  /** Dev-only: keep playing the next style. Always undefined outside development. */
  cycle?: boolean;
}

const MAX_RELEASE_ID_LENGTH = 200;

function validRelease(
  releaseId: string | null | undefined,
  builtAt: string | null | undefined,
): RunningRelease | null {
  const id = releaseId?.trim();
  if (!id || id.length > MAX_RELEASE_ID_LENGTH || !builtAt) return null;
  const time = Date.parse(builtAt);
  if (Number.isNaN(time)) return null;
  return { releaseId: id, builtAt: new Date(time).toISOString() };
}

export function resolveRunningRelease({
  releaseId,
  builtAt,
  nodeEnv,
  search,
}: {
  releaseId: string | undefined;
  builtAt: string | undefined;
  nodeEnv: string | undefined;
  /** `window.location.search`, read only in development. */
  search?: string;
}): RunningRelease | null {
  if (nodeEnv === 'development' && search) {
    const params = new URLSearchParams(search);
    const override = validRelease(
      params.get('__release'),
      params.get('__built'),
    );
    const seedParam = params.get('__seed');
    const seed =
      seedParam !== null && /^\d{1,10}$/.test(seedParam)
        ? Number(seedParam) >>> 0
        : undefined;
    const styleParam = params.get('__style');
    const style = isCelebrationStyle(styleParam) ? styleParam : undefined;
    const cycle = params.get('__cycle') === '1';
    const base = override ?? validRelease(releaseId, builtAt);
    if (!base) return null;
    return {
      ...base,
      ...(seed === undefined ? {} : { seed }),
      ...(style === undefined ? {} : { style }),
      ...(cycle ? { cycle: true } : {}),
    };
  }

  return validRelease(releaseId, builtAt);
}

/** The release this bundle was built as, read from the baked-in env. */
export function readRunningRelease(): RunningRelease | null {
  return resolveRunningRelease({
    releaseId: process.env.NEXT_PUBLIC_RELEASE_ID,
    builtAt: process.env.NEXT_PUBLIC_RELEASE_BUILT_AT,
    nodeEnv: process.env.NODE_ENV,
    search:
      process.env.NODE_ENV === 'development' && typeof window !== 'undefined'
        ? window.location.search
        : undefined,
  });
}
